/**
 * ServiceOrderScheduleConflictDialog - Conflito de horário do prestador (Fase 7 OS)
 *
 * BR-OS-008: ao agendar ou trocar o prestador, se houver choque de horário,
 * mostra os compromissos em conflito e pede um novo início (com sugestão do
 * próximo horário livre). Só confirma depois de nova verificação sem conflito.
 *
 * RELEASE: 2026-10-08 v1.0.0 - Versão inicial.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InlineNotice } from '@/components/ui/inline-notice';
import { formatStockDateTime } from '@/lib/stock-presentation';
import { isoToLocalDateTime, type ScheduleCheck } from '@/hooks/useProviderSchedule';

interface Props {
  open: boolean;
  check: ScheduleCheck | null;
  providerName: string | null;
  /** Revalida o novo início; devolve o resultado da verificação. */
  onRecheck: (start: string) => Promise<ScheduleCheck>;
  /** Confirma o novo início (já verificado sem conflito). */
  onConfirm: (start: string) => void;
  onCancel: () => void;
}

export function ServiceOrderScheduleConflictDialog({ open, check, providerName, onRecheck, onConfirm, onCancel }: Props) {
  const [start, setStart] = useState('');
  const [current, setCurrent] = useState<ScheduleCheck | null>(check);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setCurrent(check); setStart(isoToLocalDateTime(check?.suggested_start ?? null)); }
  }, [open, check]);

  const confirm = async () => {
    if (!start) return toast.error('Informe o novo horário');
    setBusy(true);
    try {
      const r = await onRecheck(start);
      if (r.conflicts.length > 0) { setCurrent(r); toast.error('Esse horário também está ocupado'); return; }
      onConfirm(start);
    } catch (e: any) {
      toast.error(e?.message ?? 'Não foi possível verificar a agenda');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Conflito na agenda do prestador</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <InlineNotice variant="warning">
            {providerName ?? 'O prestador'} já tem compromisso neste horário. Escolha outro início para a OS.
          </InlineNotice>
          <ul className="space-y-1.5">
            {(current?.conflicts ?? []).map((c) => (
              <li key={c.kind + c.id} className="rounded-md border px-3 py-2 text-sm">
                <p className="font-medium">{c.title}</p>
                <p className="text-xs text-muted-foreground">{formatStockDateTime(c.start_at)} até {formatStockDateTime(c.end_at)}</p>
              </li>
            ))}
          </ul>
          <div className="space-y-1.5">
            <Label>Novo início</Label>
            <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
            {current?.suggested_start && (
              <button type="button" className="text-xs text-primary underline-offset-2 hover:underline"
                onClick={() => setStart(isoToLocalDateTime(current.suggested_start))}>
                Usar o próximo horário livre: {formatStockDateTime(current.suggested_start)}
              </button>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Voltar</Button>
          <Button onClick={confirm} disabled={busy}>Verificar e salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
